import React, { useContext, useEffect, useState } from "react";
import { defineMessages, useIntl } from "react-intl";
import { LinkHeap } from "@app/components";
import { Svg } from "@natera/material/lib/svg";

import linkArrowIcon from "@assets/svg/icons/link-arrow.svg";
import BillingSvg from "@assets/svg/icons/billing-center.svg";
import { routes } from "@app/routing";
import { PostBillAPIContext, UserContext } from "@app/provider";
import { usePostBillHeapEvent } from "@app/postBill/hooks";

import "./billingCenterUpdatesCard.scss";
import { HEAP_EVENTS } from "@app/provider/types";
import {
  ReviewAndPayStatus,
  StatementStatus,
} from "@app/postBill/models/apiControllerModel";

const messages = defineMessages({
  billingCenter: {
    id: "billingCenter",
    defaultMessage: "Billing Center",
  },
  billingCenterDescription: {
    id: "billingCenterDescription",
    defaultMessage: "Pay my bills & review updates",
  },
});

const BillingCenterUpdatesCard: React.FC = () => {
  const intl = useIntl();
  const { getItems, verifyLogin, getCTSToken } = useContext(PostBillAPIContext);
  const { uppUser } = useContext(UserContext);
  const [billingUpdateCount, setBillingUpdateCount] = useState<number | null>(
    null
  );
  const { trackPostBillHeapEvent } = usePostBillHeapEvent();

  const getBillingCenterItems = async () => {
    try {
      const billingCenterItems = await getItems();
      if (billingCenterItems.data) {
        if (
          billingCenterItems.data.balanceDateStatus !==
          ReviewAndPayStatus.PAYMENTS_WAITING
        ) {
          setBillingUpdateCount(0);
        } else {
          setBillingUpdateCount(
            billingCenterItems.data.readyToPay.filter(
              (item) => item.statementStatus !== StatementStatus.PAYMENT_PENDING
            ).length
          );
        }
      }
    } catch (error) {
      console.error("Failed to get billing center items: ", error);
    }
  };

  const ctsToken = getCTSToken?.();

  useEffect(() => {
    const verify = async () => {
      if (!ctsToken && uppUser?.patientUID) {
        try {
          await verifyLogin();
        } catch (error) {
          console.error("Verify login failed: ", error);
        }
      }
      trackPostBillHeapEvent({
        eventKey: HEAP_EVENTS.upp_view_billingcenter_card,
      });
    };

    verify();
  }, [uppUser?.patientUID]);

  useEffect(() => {
    if (billingUpdateCount === null && ctsToken) {
      getBillingCenterItems();
    }
  }, [ctsToken, billingUpdateCount]);

  return (
    <div
      className={
        billingUpdateCount ? "billing-center-updates-card-container" : ""
      }
      data-testid="billing-center-updates-card-container"
    >
      {!!billingUpdateCount && (
        <LinkHeap
          to={routes.billingCenter}
          className="billing-center-updates-card"
          heapEventName={HEAP_EVENTS.upp_click_billingcenter_card}
          heapEventProps={{
            identity: uppUser?.patientUID,
          }}
        >
          <Svg
            className="billing-center-updates-card__icon"
            content={BillingSvg}
          />

          <div className="billing-center-updates-card__text">
            <span>{intl.formatMessage(messages.billingCenter)}</span>

            <span className="billing-center-updates-card__text__description">
              {intl.formatMessage(messages.billingCenterDescription)}
            </span>
          </div>

          {billingUpdateCount > 0 && (
            <span className="billing-center-updates-card__count">
              {billingUpdateCount}
            </span>
          )}

          <Svg
            className="billing-center-updates-card__arrow"
            content={linkArrowIcon}
          />
        </LinkHeap>
      )}
    </div>
  );
};

export default BillingCenterUpdatesCard;
